import {
  ExtensionTaskCompleteBodySchema,
  ExtensionTaskCompleteOutputSchema,
  ExtensionTaskCompleteParamsSchema,
  ExtensionTaskSchema,
  ExtensionTasksPollOutputSchema,
  ExtensionTasksPollParamsSchema,
  ExtensionTimelineSyncBodySchema,
  ExtensionTimelineSyncOutputSchema,
} from '@sweetistics/contracts';
import type { z } from 'zod';
import { ensureDate } from '@/lib/utils/date-coercion';

export type ExtensionTaskInput = z.input<typeof ExtensionTaskSchema>;
export type ExtensionTaskOutput = z.output<typeof ExtensionTaskSchema>;

function normalizeTaskDates(task: ExtensionTaskInput): ExtensionTaskOutput {
  return ExtensionTaskSchema.parse({
    ...task,
    status: task.status,
    createdAt: normalizeDate(task.createdAt),
    startedAt: normalizeNullableDate(task.startedAt),
    completedAt: normalizeNullableDate(task.completedAt),
    updatedAt: normalizeDate(task.updatedAt),
  });
}

export function serializeExtensionTasksResponse(
  tasks: ExtensionTaskInput[]
): z.output<typeof ExtensionTasksPollOutputSchema> {
  return ExtensionTasksPollOutputSchema.parse({
    tasks: tasks.map((task) => normalizeTaskDates(task)),
  });
}

export function serializeExtensionTaskCompleteResponse(
  task: ExtensionTaskInput
): z.output<typeof ExtensionTaskCompleteOutputSchema> {
  return ExtensionTaskCompleteOutputSchema.parse({
    success: true,
    task: normalizeTaskDates(task),
  });
}

export function validateExtensionTaskCompleteParams(
  parameters: unknown
): z.output<typeof ExtensionTaskCompleteParamsSchema> {
  return ExtensionTaskCompleteParamsSchema.parse(parameters);
}

export function validateExtensionTaskCompleteBody(body: unknown): z.output<typeof ExtensionTaskCompleteBodySchema> {
  return ExtensionTaskCompleteBodySchema.parse(body);
}

export function validateExtensionTasksParams(parameters: unknown): z.output<typeof ExtensionTasksPollParamsSchema> {
  return ExtensionTasksPollParamsSchema.parse(parameters);
}

export function validateExtensionTimelineSyncBody(body: unknown): z.output<typeof ExtensionTimelineSyncBodySchema> {
  return ExtensionTimelineSyncBodySchema.parse(body);
}

export function serializeExtensionTimelineSyncResponse(
  payload: z.input<typeof ExtensionTimelineSyncOutputSchema>
): z.output<typeof ExtensionTimelineSyncOutputSchema> {
  return ExtensionTimelineSyncOutputSchema.parse(payload);
}

function normalizeDate(value: unknown): string {
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : value;
  }
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return ensureDate(value as Date | string | number | null | undefined, new Date()).toISOString();
}

function normalizeNullableDate(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  return normalizeDate(value);
}
